import { useEffect, useState } from 'react';
import axios from 'axios';
import { Video, Plus, Edit, Trash2, Loader2, X, Save, Youtube, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['Engineering', 'JEE', 'NEET'];
const SUBJECTS = ['Physics', 'Chemistry', 'Mathematics', 'Biology', 'Computer Science', 'Electronics', 'Electrical', 'General'];

export default function AdminVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtube_url: '',
    category: 'Engineering',
    subject: 'Physics'
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/admin/videos`, getAuthHeaders());
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      await axios.delete(`${API}/admin/videos/${videoId}`, getAuthHeaders());
      toast.success('Video deleted successfully');
      fetchVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Failed to delete video');
    }
  };

  const openCreateModal = () => {
    setEditingVideo(null);
    setFormData({
      title: '',
      description: '',
      youtube_url: '',
      category: 'Engineering',
      subject: 'Physics'
    });
    setShowModal(true);
  };

  const openEditModal = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description,
      youtube_url: video.youtube_url,
      category: video.category,
      subject: video.subject
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.youtube_url.trim()) {
      toast.error('Please fill in title and YouTube URL');
      return;
    }

    setSaving(true);
    try {
      if (editingVideo) {
        await axios.put(`${API}/admin/videos/${editingVideo.video_id}`, formData, getAuthHeaders());
        toast.success('Video updated successfully');
      } else {
        await axios.post(`${API}/admin/videos`, formData, getAuthHeaders());
        toast.success('Video added successfully');
      }

      setShowModal(false);
      fetchVideos();
    } catch (error) {
      console.error('Error saving video:', error);
      toast.error('Failed to save video');
    } finally {
      setSaving(false);
    }
  };

  const getYouTubeThumbnail = (url) => {
    const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return videoId ? `https://img.youtube.com/vi/${videoId[1]}/mqdefault.jpg` : null;
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return videoId ? `https://www.youtube.com/embed/${videoId[1]}` : null;
  };

  const groupByCategory = () => {
    const grouped = {};
    videos.forEach((video) => {
      if (!grouped[video.category]) {
        grouped[video.category] = [];
      }
      grouped[video.category].push(video);
    });
    return grouped;
  };

  const groupedVideos = groupByCategory();

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-brand-dark font-display">Video Lectures</h1>
          <p className="text-slate-600 mt-2">Total Videos: {videos.length}</p>
        </div>
        <Button data-testid="create-video-btn" onClick={openCreateModal} className="btn-primary">
          <Plus className="h-5 w-5 mr-2" />
          Add Video
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="inline-block h-8 w-8 animate-spin text-brand-blue" />
        </div>
      ) : videos.length === 0 ? (
        <div className="text-center py-12">
          <Video className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 mb-4">No videos added yet.</p>
          <Button onClick={openCreateModal} className="btn-primary">
            <Plus className="h-5 w-5 mr-2" />
            Add Your First Video
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.keys(groupedVideos).sort().map((category) => (
            <div key={category}>
              <h2 className="text-xl font-bold text-brand-dark mb-4 pb-2 border-b-2 border-brand-blue">
                {category}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groupedVideos[category].map((video) => (
                  <div key={video.video_id} className="border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                    <div className="relative">
                      {getYouTubeThumbnail(video.youtube_url) ? (
                        <img 
                          src={getYouTubeThumbnail(video.youtube_url)} 
                          alt={video.title}
                          className="w-full h-40 object-cover"
                        />
                      ) : (
                        <div className="w-full h-40 bg-slate-100 flex items-center justify-center">
                          <Youtube className="h-12 w-12 text-slate-300" />
                        </div>
                      )}
                      <a 
                        href={video.youtube_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity"
                      >
                        <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center">
                          <Youtube className="h-6 w-6 text-white" />
                        </div>
                      </a>
                    </div>
                    <div className="p-4">
                      <span className="text-xs bg-brand-blue/10 text-brand-blue px-2 py-1 rounded">{video.subject}</span>
                      <h3 className="text-md font-semibold text-brand-dark mt-2 line-clamp-2">{video.title}</h3>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{video.description}</p>
                      <div className="flex gap-2 mt-3">
                        <a href={video.youtube_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                          <Button variant="outline" size="sm" className="w-full">
                            <ExternalLink className="h-4 w-4 mr-1" /> Watch
                          </Button>
                        </a>
                        <Button onClick={() => openEditModal(video)} variant="outline" size="sm">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button onClick={() => handleDelete(video.video_id)} variant="destructive" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg overflow-hidden">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-brand-dark">
                {editingVideo ? 'Edit Video' : 'Add New Video'}
              </h2>
              <Button onClick={() => setShowModal(false)} variant="ghost" size="sm">
                <X className="h-5 w-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <Label>Video Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Introduction to Thermodynamics"
                />
              </div>

              <div>
                <Label>YouTube URL *</Label>
                <Input
                  value={formData.youtube_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, youtube_url: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=..."
                />
                {formData.youtube_url && getYouTubeThumbnail(formData.youtube_url) && (
                  <img 
                    src={getYouTubeThumbnail(formData.youtube_url)} 
                    alt="Preview"
                    className="mt-2 w-full h-32 object-cover rounded"
                  />
                )}
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full h-10 px-3 border border-slate-200 rounded-md"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Subject</Label>
                  <select
                    value={formData.subject}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full h-10 px-3 border border-slate-200 rounded-md"
                  >
                    {SUBJECTS.map(sub => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" onClick={() => setShowModal(false)} variant="outline">Cancel</Button>
                <Button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  {editingVideo ? 'Update' : 'Add Video'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
